import React, { useEffect, useState, useRef } from "react";
import { decryptDetails } from "./utils/encryption"; // Decryption logic
import PrivacyPolicy from "./components/PrivacyPolicy"; // Privacy policy modal
import CameraPermission from "./components/CameraPermission"; // Camera/microphone permissions
import WaitingRoom from "./components/WaitingRoom"; // Waiting room component
import VideoRoom from "./components/VideoRoom"; // Video room component
import InvalidLinkPage from "./components/InvalidLinkPage"; // Invalid link page
import { io } from "socket.io-client"; // Signaling server client

const App = () => {
  const [patientDetails, setPatientDetails] = useState(null); // Decrypted patient details
  const [permissionsGranted, setPermissionsGranted] = useState(false); // Camera/microphone permissions
  const [privacyAccepted, setPrivacyAccepted] = useState(false); // Privacy policy acceptance
  const [errorMessage, setErrorMessage] = useState(""); // Error message for invalid URLs
  const [doctorAvailable, setDoctorAvailable] = useState(false); // Doctor's availability
  const [turnConfig, setTurnConfig] = useState(null); // TURN credentials
  const [peerConnection, setPeerConnection] = useState(null); // WebRTC PeerConnection
  const [inVideoRoom, setInVideoRoom] = useState(false); // To switch between WaitingRoom and VideoRoom
  const socketRef = useRef(null); // Ref for socket instance
  const encryptionKey = process.env.REACT_APP_ENCRYPTION_KEY; // Encryption key from .env file
  const signalingServerUrl = "https://signaling.promedhms.com"; // Signaling server URL
  const [doctorSocketId, setdoctorSocketId] = useState(null); // Doctor Socket ID

  // Step 1: Decrypt patient details from the URL
  useEffect(() => {
    const decryptPatientDetails = () => {
      const urlPath = window.location.pathname.slice(1); // Remove leading slash
      console.log("URL Path:", urlPath); // Debug log for URL path

      if (!urlPath) {
        console.error("No encrypted data found in the URL.");
        setErrorMessage("Invalid or missing session details in the URL.");
        return;
      }

      try {
        const decryptedData = decryptDetails(urlPath, encryptionKey); // Decrypt URL data
        console.log("Decrypted Patient Details:", decryptedData); // Debug decrypted details
        setPatientDetails(decryptedData); // Store decrypted details
      } catch (error) {
        console.error("Failed to decrypt patient details:", error.message);
        setErrorMessage("Invalid link. Please contact support.");
      }
    };

    decryptPatientDetails();
  }, [encryptionKey]);

  // Step 2: Fetch Dynamic TURN Credentials
  useEffect(() => {
    const fetchTurnCredentials = async () => {
      try {
        const response = await fetch(
          "https://promed.in.net/team/teleconf/dyn-turn-auth/get_turn_credentials.php"
        );
        if (!response.ok) {
          throw new Error(`Failed to fetch TURN credentials: ${response.statusText}`);
        }
        const data = await response.json();

        // Use the iceServers array directly from the response
        console.log("Fetched TURN Credentials:", data);

        // Set TURN configuration
        setTurnConfig({
          iceServers: data.iceServers, // Use the provided iceServers directly
        });
      } catch (error) {
        console.error("Error fetching TURN credentials:", error.message);
        setErrorMessage("Failed to configure video connection. Please try again.");
      }
    };

    fetchTurnCredentials();
  }, []);

  // Step 3: Connect to Signaling Server and Check Doctor's Availability
  useEffect(() => {
    if (patientDetails) {
      console.log("Initializing socket connection...");

      // Initialize socket connection
      socketRef.current = io(signalingServerUrl);

      // Join the room after connection
      socketRef.current.emit("joinRoom", {
        roomId: patientDetails.room_id || "test-room", // Replace with your test room ID
        role: "patient",
      });

      socketRef.current.emit('checkRoomStatus', { roomId: 'test-room' }, (response) => {
        console.log('checkRoomStatus Room Status:', response);
        if (response.doctorAvailable) {
            setDoctorAvailable(true);
            console.log('Doctor is available in the room.');
        }
        else
        {
          setDoctorAvailable(false);
        }
    });

      // Listen for doctor availability
      socketRef.current.on("doctorAvailable", () => {
        console.log("Doctor is now available in the room.");
        setDoctorAvailable(true);
      });

      socketRef.current.on("doctorUnavailable", () => {
        console.log("Doctor is not available in the room.");
        setDoctorAvailable(false);
      });

      // Listen for signaling events (SDP offer, ICE candidates)
      socketRef.current.on("signal", async (data) => {
        if (!peerConnection) {
          console.error("PeerConnection not initialized.");
          return;
        }

        const { signalData } = data;

        if (signalData.type === "offer") {
          console.log("Received SDP Offer:", signalData);

          // Set the remote description
          await peerConnection.setRemoteDescription(new RTCSessionDescription(signalData));

          // Create and send SDP answer
          const answer = await peerConnection.createAnswer();
          await peerConnection.setLocalDescription(answer);

          setdoctorSocketId(data.from);

          socketRef.current.emit("signal", {
            to: data.from, // Send back to the doctor
            signalData: peerConnection.localDescription,
          });
          console.log("Sent SDP Answer:", peerConnection.localDescription);
        }

        if (signalData.candidate) {
          console.log("Received ICE Candidate:", signalData);
          await peerConnection.addIceCandidate(new RTCIceCandidate(signalData));
        }
      });

      // Cleanup on component unmount
      return () => socketRef.current.disconnect();
    }
  }, [patientDetails, peerConnection]);

  // Step 4: Initialize PeerConnection
  useEffect(() => {
    if (turnConfig) {
      const pc = new RTCPeerConnection(turnConfig);

      // Handle incoming tracks
      pc.ontrack = (event) => {
        console.log("Received Remote Stream:", event.streams[0]);
        // Handle remote video stream (e.g., pass it to VideoRoom)
      };

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate) {
          socketRef.current.emit("signal", {
            to: "doctor", // Replace with the actual doctor's socket ID
            signalData: event.candidate,
          });
          console.log("Sent ICE Candidate:", event.candidate);
        }
      };

      setPeerConnection(pc);
    }
  }, [turnConfig]);

  // Handle Connecting to Video Room
  const handleConnectToVideoRoom = () => {
    if (doctorAvailable) {
      console.log("Connecting to Video Room...");
      console.log("Connecting to Video Room with doctorSocketId ", doctorSocketId);
      setInVideoRoom(true); // Switch to VideoRoom view
    } else {
      alert("Doctor is not yet available. Please wait.");
    }
  };

  return (
    <div>
      <h1>Telemedicine Platform</h1>

      {/* Show error messages if any */}
      {errorMessage && <InvalidLinkPage message={errorMessage} />}

      {/* Privacy Policy */}
      {!privacyAccepted && patientDetails && !errorMessage && (
        <PrivacyPolicy onAccept={() => setPrivacyAccepted(true)} />
      )}

      {/* Camera Permissions */}
      {privacyAccepted && !permissionsGranted && (
        <CameraPermission onPermissionGranted={setPermissionsGranted} />
      )}

      {/* Waiting Room or Video Room */}
      {privacyAccepted && permissionsGranted && turnConfig && (
        <>
          {inVideoRoom ? (
            <VideoRoom
              patientDetails={patientDetails}
              turnConfig={turnConfig}
              peerConnection={peerConnection}
              socket={socketRef.current}
              doctorsocketid={doctorSocketId}
            />
          ) : (
            <WaitingRoom onConnect={handleConnectToVideoRoom} />
          )}
        </>
      )}
    </div>
  );
};

export default App;
